import axios from 'axios';
import React from 'react';

import { wait } from 'lib/wait';

export type DownloadFileHookInput = {
	url: string;
	filename: string;
	downloadFn?(url: string): Promise<any>;
};

type DownloadState = 'pending' | 'loading' | 'error' | 'finished';

type DownloadFilesFn = (
	downloadFiles: DownloadFileHookInput[],
	options?: Partial<{
		waitForCompletion: boolean;
		onSuccess: () => void;
		onError: (e: Error) => void;
	}>
) => Promise<void>;

type DownloadElement = {
	url: string;
	filename: string;
};

const triggerDownload = ({ url, filename }: DownloadElement) => {
	const targetFileName = filename;
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', targetFileName);
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const doDownloads = async (downloads: DownloadElement[]) => {
	const remainingDownloads = [...downloads];
	const hasPendingDownloads = () => remainingDownloads.length > 0;
	while (hasPendingDownloads()) {
		const downloadElement = remainingDownloads.pop();
		triggerDownload(downloadElement);
		if (hasPendingDownloads()) await wait(1000);
	}
};

export const useDownloadFiles = () => {
	const mounted = React.useRef(true);
	const lastCallId = React.useRef(0);
	const [state, setState] = React.useState<DownloadState>('pending');

	React.useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	const downloadFiles: DownloadFilesFn = React.useCallback(async (downloadFiles, options = {}) => {
		setState('loading');
		const { onSuccess, onError, waitForCompletion = true } = options;
		const callId = ++lastCallId.current;
		const canUpdate = () => callId === lastCallId.current && mounted.current;

		const completedHandler = () => {
			setState('finished');
			if (onSuccess) onSuccess();
		};

		const errorHandler = (e: Error) => {
			setState('error');
			if (onError) onError(e);
		};

		if (waitForCompletion) {
			const downloadPromises = downloadFiles.map(async downloadFile => {
				const response = downloadFile.downloadFn
					? await downloadFile.downloadFn(downloadFile.url)
					: await axios.get(downloadFile.url, { responseType: 'blob' });
				return { response, downloadFile };
			});
			try {
				const responses = await Promise.all(downloadPromises);
				if (!canUpdate()) return;
				responses.forEach(({ response, downloadFile: { filename } }) => {
					const targetFileName = filename;
					// IE variant
					// @ts-ignore
					if (window.navigator && window.navigator.msSaveOrOpenBlob) {
						// @ts-ignore
						window.navigator.msSaveOrOpenBlob(new Blob([response.data], { type: response.headers['content-type'] }), targetFileName);
					} else {
						const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
						triggerDownload({ url, filename: targetFileName });
					}
				});
				completedHandler();
			} catch (e) {
				if (!canUpdate()) return;
				errorHandler(e);
			}
		} else {
			await doDownloads(downloadFiles);
			completedHandler();
		}
	}, []);

	return { state, downloadFiles };
};
